import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { appState, type AppStateData, type Tab } from '../store/AppState';
import { offlineService } from '../services/offline';

import './SyncIndicator';
import './PitchCounter';
import './StatsView';
import './AuthButton';
import './ThemeSwitcher';
import './admin/AdminView';
import './shared/Toast';
import './shared/SignInOptions';
import './ObserverView';
import './PublicGameView';
import './InviteRedemption';
import './CoachOnboarding';
import '@khmyznikov/pwa-install';

@customElement('pitch-app')
export class PitchApp extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state()
  private appStateData: AppStateData = appState.getState();

  @state()
  private observerToken: string | null = null;

  @state()
  private isObserverRoute = false;

  @state()
  private inviteToken: string | null = null;

  @state()
  private isInviteRoute = false;

  /**
   * Two-step sign-in flow:
   *  - 'choose': role chooser (coach vs browse) — landing default
   *  - 'sign-in': sign-in card after user identifies as a coach
   * Back button on the sign-in card resets to 'choose'. No URL state — the
   * chooser is the consistent landing for every unauthenticated visit.
   */
  @state()
  private signInStep: 'choose' | 'sign-in' = 'choose';

  connectedCallback() {
    super.connectedCallback();
    appState.subscribe((state) => {
      this.appStateData = state;
    });

    // Check for observer route
    this.handleRouting();
    window.addEventListener('popstate', () => this.handleRouting());

    // Initialize offline service
    offlineService.init();
  }

  private handleRouting() {
    const path = window.location.pathname;
    const search = window.location.search;

    // Check for /observe route with optional token query param
    if (path === '/observe') {
      this.isObserverRoute = true;
      const params = new URLSearchParams(search);
      this.observerToken = params.get('token');
      return;
    }

    // Also support /observe/:token path format
    const pathMatch = path.match(/^\/observe\/([a-zA-Z0-9_=-]+)$/);
    if (pathMatch) {
      this.isObserverRoute = true;
      this.observerToken = pathMatch[1];
      return;
    }

    // Check for /invite route
    if (path === '/invite') {
      this.isInviteRoute = true;
      const params = new URLSearchParams(search);
      this.inviteToken = params.get('token');
      return;
    }

    // Not a special route
    this.isObserverRoute = false;
    this.observerToken = null;
    this.isInviteRoute = false;
    this.inviteToken = null;

    // Re-read public-mode flags from the URL so browser Back/Forward (which
    // fire popstate without going through setPublicMode) update the view.
    appState.syncPublicStateFromUrl();

    // In public mode, force the Stats tab — Count/Admin are unreachable.
    if (this.appStateData.publicMode) {
      if (this.activeTab !== 'stats') {
        appState.setActiveTab('stats', { updateUrl: false });
      }
      return;
    }

    // Parse tab from URL and restore it (use replaceState to avoid adding history entry on load)
    const tabFromUrl = appState.getTabFromUrl();
    const gameFromUrl = appState.getGameIdFromUrl();

    if (tabFromUrl && tabFromUrl !== this.activeTab) {
      appState.setActiveTab(tabFromUrl, { updateUrl: false });
    } else if (!tabFromUrl && gameFromUrl && this.activeTab !== 'count') {
      // If there's a game param but no tab param, switch to count tab (game context)
      appState.setActiveTab('count', { updateUrl: false });
    }
  }

  private get isLoading() {
    return this.appStateData.auth.loading;
  }

  private get isAuthenticated() {
    return this.appStateData.auth.user !== null;
  }

  private get isPublicMode() {
    return this.appStateData.publicMode;
  }

  private get publicGameId() {
    return this.appStateData.publicGameId;
  }

  private enterPublicMode() {
    appState.setPublicMode(true);
  }

  private exitPublicMode() {
    appState.setPublicMode(false);
  }

  private get isAdmin() {
    return this.appStateData.auth.user?.is_admin ?? false;
  }

  private get hasNoTeams() {
    return appState.hasNoTeams;
  }

  private get roleBadge(): string {
    if (this.isAdmin) return '';
    const teams = appState.userTeams;
    if (teams.length === 1) return `Coach - ${teams[0].team_name}`;
    if (teams.length > 1) return `Coach - ${teams.length} teams`;
    return '';
  }

  private get activeTab() {
    return this.appStateData.activeTab;
  }

  private get hasActiveGame() {
    return this.appStateData.currentGame !== null;
  }

  private setTab(tab: Tab) {
    // If clicking count tab while already on count (with active game), go back to game selection
    if (tab === 'count' && this.activeTab === 'count' && this.hasActiveGame) {
      appState.setCurrentGame(null);
      return;
    }
    appState.setActiveTab(tab);
  }

  render() {
    // Observer mode bypasses authentication
    if (this.isObserverRoute) {
      return this.renderObserverMode();
    }

    // Invite mode - can work with or without auth
    if (this.isInviteRoute) {
      return this.renderInviteMode();
    }

    // Public (guest) mode overrides the auth gate. The Firebase auth
    // listener still runs in the background, but we ignore its state
    // here so a stale Firebase session doesn't flip a guest browser
    // into the authenticated tree mid-browse.
    if (this.isPublicMode) {
      return this.renderPublicMode();
    }

    if (this.isLoading) {
      return html`
        <div class="min-h-screen flex flex-col items-center justify-center gap-4 ">
          <div class="w-30 h-30 bg-base-100 rounded-sm flex items-center justify-center shadow-lg animate-pulse p-2">
            <img src="/static/image/logo-no-text.webp" alt="Baseball Manitoba" class="w-full h-full object-contain" />
          </div>
          <span class="loading loading-dots loading-lg text-white/80"></span>
        </div>
      `;
    }

    if (!this.isAuthenticated) {
      return this.renderSignIn();
    }

    return html`
      <div class="min-h-screen flex flex-col main-content pb-20">
        <!-- Global toast container -->
        <toast-container></toast-container>

        <!-- Coach onboarding modal -->
        <coach-onboarding></coach-onboarding>

        <!-- Header -->
        <header class="navbar bg-base-100/95 backdrop-blur-sm shadow-sm sticky top-0 z-40 border-b border-base-200/50">
          <div class="flex-1">
            <a href="/" class="flex items-center gap-3 px-2">
              <div class="w-13 h-12 rounded-sm flex items-center justify-center overflow-hidden">
                <img src="/static/image/logo-no-text.webp" alt="Baseball Manitoba" class="w-full h-full object-contain" />
              </div>
              <div>
                <span class="hidden sm:inline text-xs text-base-content/50 ml-2">Baseball Manitoba</span>
                ${this.roleBadge ? html`
                  <span class="hidden sm:inline badge badge-secondary badge-sm ml-2">${this.roleBadge}</span>
                ` : nothing}
              </div>
            </a>
          </div>
          <div class="flex-none gap-1">
            <sync-indicator></sync-indicator>
            <theme-switcher></theme-switcher>
            <auth-button></auth-button>
          </div>
        </header>

        <!-- Main content -->
        <main class="flex-1 p-4 w-full max-w-4xl mx-auto">
          ${this.renderTabContent()}
        </main>

        <!-- Bottom navigation -->
        <nav class="bottom-nav">
          <button
            class=${this.activeTab === 'stats' ? 'active' : ''}
            @click=${() => this.setTab('stats')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span class="btm-nav-label">Stats</span>
          </button>

          <button
            class=${this.activeTab === 'count' ? 'active' : ''}
            @click=${() => this.setTab('count')}
          >
            ${this.hasActiveGame ? html`
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span class="btm-nav-label">Home</span>
            ` : html`
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              <span class="btm-nav-label">Count</span>
            `}
          </button>

          ${this.isAdmin ? html`
            <button
              class=${this.activeTab === 'admin' ? 'active' : ''}
              @click=${() => this.setTab('admin')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span class="btm-nav-label">Admin</span>
            </button>
          ` : nothing}
        </nav>

        <!-- PWA Install Prompt -->
        <pwa-install
          manifest-url="/static/manifest.json"
          icon="/static/icons/icon-512x512.png"
          install-description="Install for quick access to pitch tracking"
        ></pwa-install>
      </div>
    `;
  }

  private renderTabContent() {
    // Show empty state for users with no team assignments (non-admin)
    if (this.hasNoTeams && this.activeTab === 'count') {
      return html`
        <div class="flex flex-col items-center justify-center min-h-[50vh] gap-4 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 text-base-content/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <h3 class="text-lg font-semibold">No Team Assigned</h3>
          <p class="text-base-content/60 max-w-sm">
            You're not assigned to any teams yet. Ask your league administrator for an invite link to join a team.
          </p>
        </div>
      `;
    }

    switch (this.activeTab) {
      case 'stats':
        return html`<stats-view></stats-view>`;
      case 'count':
        return html`<pitch-counter></pitch-counter>`;
      case 'admin':
        return this.isAdmin ? html`<admin-view></admin-view>` : nothing;
      default:
        return nothing;
    }
  }

  private goToSignIn = () => {
    this.signInStep = 'sign-in';
  };

  private goToChooser = () => {
    this.signInStep = 'choose';
  };

  private renderSignIn() {
    return html`
      <div class="sign-in-page min-h-screen flex items-center justify-center px-4 py-8 sm:py-12 relative overflow-hidden">
        <!-- Animated gradient backdrop -->
        <div class="sign-in-bg" aria-hidden="true"></div>
        <!-- Faint baseball-stitch motif overlay -->
        <div class="sign-in-motif" aria-hidden="true"></div>

        <div class="relative w-full max-w-md flex flex-col items-center">
          <!-- Logo -->
          <div class="sign-in-logo mb-5">
            <img
              src="/static/image/logo-no-text.webp"
              alt="Baseball Manitoba"
              class="w-full h-full object-contain drop-shadow-lg"
            />
          </div>

          <!-- Brand + tagline -->
          <div class="text-center mb-6 sign-in-heading">
            <p class="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-white/80 mb-1">
              Baseball Manitoba
            </p>
            <h1 class="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Pitch Counter
            </h1>
            <p class="mt-2 text-sm sm:text-base text-white/85 max-w-xs mx-auto">
              Pitch count tracking for Manitoba youth baseball.
            </p>
          </div>

          <!-- Step panel: chooser OR sign-in card. Both consume the same
               visual slot — only one renders at a time so screen-reader
               focus and tab order stay predictable. -->
          ${this.signInStep === 'choose'
            ? this.renderChooserStep()
            : this.renderSignInStep()}

          <!-- Trust footer (anchored to both steps) -->
          <p class="sign-in-footer mt-6 text-xs text-white/70 text-center">
            Official app of Baseball Manitoba
          </p>
        </div>
      </div>
    `;
  }

  private renderChooserStep() {
    return html`
      <div class="sign-in-step sign-in-chooser w-full">
        <p class="sign-in-chooser-caption text-center text-sm text-white/85 mb-4">
          How will you use Pitch Counter?
        </p>

        <div class="flex flex-col gap-3 w-full">
          <button
            type="button"
            class="role-card group"
            aria-label="I'm a coach — sign in to track your team's pitches"
            @click=${this.goToSignIn}
          >
            <span class="role-card-icon">
              <!-- Clipboard with check: established coach/tracking pattern in this codebase -->
              <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
              </svg>
            </span>
            <span class="role-card-body">
              <span class="role-card-title">I'm a coach</span>
              <span class="role-card-subtitle">Sign in to track your team's pitches</span>
            </span>
            <svg xmlns="http://www.w3.org/2000/svg" class="role-card-chevron" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
            </svg>
          </button>

          <button
            type="button"
            class="role-card browse-public-btn group"
            aria-label="Browse games — view teams, scores, and pitch counts without signing in"
            @click=${this.enterPublicMode}
          >
            <span class="role-card-icon">
              <!-- Chart-bar: same icon StatsView/PublicGameView use as the "view stats" anchor -->
              <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </span>
            <span class="role-card-body">
              <span class="role-card-title">Browse games</span>
              <span class="role-card-subtitle">View teams, scores, and pitch counts</span>
            </span>
            <svg xmlns="http://www.w3.org/2000/svg" class="role-card-chevron" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  private renderSignInStep() {
    return html`
      <div class="sign-in-step w-full">
        <div class="card bg-base-100 shadow-2xl w-full sign-in-card">
          <div class="card-body px-6 py-6 sm:px-8 sm:py-7 gap-0">
            <button
              type="button"
              class="sign-in-back-btn inline-flex items-center gap-1 text-sm text-base-content/70 hover:text-primary focus-visible:text-primary transition-colors self-start -ml-2 px-2 py-1 rounded min-h-[44px]"
              aria-label="Back to options"
              @click=${this.goToChooser}
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
              </svg>
              <span>Back</span>
            </button>

            <div class="flex flex-col items-center text-center mt-2">
              <sign-in-options></sign-in-options>

              <div class="w-full mt-5 pt-4 border-t border-base-200">
                <button
                  type="button"
                  class="browse-public-btn inline-flex items-center gap-1.5 text-sm text-base-content/70 hover:text-primary focus-visible:text-primary transition-colors group min-h-[44px] px-2"
                  aria-label="Browse teams and games without signing in"
                  @click=${this.enterPublicMode}
                >
                  <span>No account? Browse teams &amp; games</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                    aria-hidden="true"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderPublicMode() {
    return html`
      <div class="min-h-screen flex flex-col main-content pb-20 animate-fade-in">
        <toast-container></toast-container>

        <header class="navbar bg-base-100/95 backdrop-blur-sm shadow-sm sticky top-0 z-40 border-b border-base-200/50">
          <div class="flex-1">
            <a href="/" class="flex items-center gap-3 px-2">
              <div class="w-13 h-12 rounded-sm flex items-center justify-center overflow-hidden">
                <img src="/static/image/logo-no-text.webp" alt="Baseball Manitoba" class="w-full h-full object-contain" />
              </div>
              <div>
                <span class="hidden sm:inline text-xs text-base-content/50 ml-2">Baseball Manitoba</span>
                <span class="badge badge-ghost badge-sm ml-2">Guest</span>
              </div>
            </a>
          </div>
          <div class="flex-none gap-1">
            <theme-switcher></theme-switcher>
            <button
              type="button"
              class="btn btn-primary btn-sm exit-public-btn"
              aria-label="Sign in to access counter mode"
              @click=${this.exitPublicMode}
            >
              Sign in
            </button>
          </div>
        </header>

        <main class="flex-1 p-4 w-full max-w-4xl mx-auto">
          <div class="alert alert-info alert-sm py-2 mb-4 text-sm public-mode-banner" role="status">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span>
              You're browsing as a guest.
              <button
                type="button"
                class="link link-primary font-semibold ml-1 public-banner-signin"
                @click=${this.exitPublicMode}
              >Sign in</button>
              to access counter mode and coach features.
            </span>
          </div>

          ${this.publicGameId
            ? html`<public-game-view game-id=${this.publicGameId}></public-game-view>`
            : html`<stats-view></stats-view>`}
        </main>
      </div>
    `;
  }

  private renderInviteMode() {
    return html`
      <div class="min-h-screen flex flex-col bg-base-200">
        <header class="navbar bg-base-100 shadow-sm border-b border-base-200">
          <div class="flex-1">
            <a href="/" class="flex items-center gap-3 px-2">
              <div class="w-10 h-10 rounded-sm flex items-center justify-center overflow-hidden">
                <img src="/static/image/logo-no-text.webp" alt="Baseball Manitoba" class="w-full h-full object-contain" />
              </div>
              <span class="font-semibold text-lg">Pitch Counter</span>
            </a>
          </div>
          <div class="flex-none">
            <theme-switcher></theme-switcher>
          </div>
        </header>
        <main class="flex-1">
          <invite-redemption .token=${this.inviteToken ?? ''}></invite-redemption>
        </main>
      </div>
    `;
  }

  private renderObserverMode() {
    return html`
      <div class="min-h-screen flex flex-col bg-base-200">
        <!-- Simple header for observer mode -->
        <header class="navbar bg-base-100 shadow-sm border-b border-base-200">
          <div class="flex-1">
            <a href="/" class="flex items-center gap-3 px-2">
              <div class="w-10 h-10 rounded-sm flex items-center justify-center overflow-hidden">
                <img src="/static/image/logo-no-text.webp" alt="Baseball Manitoba" class="w-full h-full object-contain" />
              </div>
              <span class="font-semibold text-lg">Pitch Counter</span>
            </a>
          </div>
          <div class="flex-none">
            <theme-switcher></theme-switcher>
          </div>
        </header>

        <!-- Observer view content -->
        <main class="flex-1">
          <observer-view .token=${this.observerToken}></observer-view>
        </main>
      </div>
    `;
  }
}
